import React, { useState } from 'react';
import { useIntl } from "react-intl";
import { AgentCreate, LocaleType, LocaleTypes, LabelKeyValueItem, getLabelKeyFromValue, LocaleIntlKeys } from "@/model";
import { useCreateAgent } from "@/query/agent";
import { useModal, useRouteHandler } from "@/hook";
import Button from "@/component/Button";
import { Modal } from "@/component/modal";
import { errorToast, successToast } from "@/component/Toast";
import { TextInputGroup, RangeSlider, DropdownGroup, DropdownButton, DropdownItem, DropdownItems } from "@/component/input";


export const AGENT_CREATE_MODAL_ID = "agent-create-Modal-id";

const EMBEDDING_OPTIONS: LabelKeyValueItem[] = [
    { value: "use_v3", labelKey: "i01005_o1" },
    { value: "ada", labelKey: "i01005_o2" },
];

const AgentCreateModal: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const { moveToDashboard } = useRouteHandler();

    const [name, setName] = useState<string>("");
    const [locale, setLocale] = useState<LocaleType>("ko");
    const [embedding, setEmbedding] = useState<"use_v3" | "ada">("use_v3");
    const [deepFinderThreshold, setDeepFinderThreshold] = useState<number>(50);
    const [deepFinderResultCount, setDeepFinderResultCount] = useState<number>(3);
    const [isValidated, setIsValidated] = useState<boolean>(false);

    const { mutateAsync: createAgent, isPending: isCreating } = useCreateAgent();

    const submit = async () => {
        setIsValidated(true);

        const form: AgentCreate = {
            agent_name: name.trim(),
            language: locale,
            embedding: embedding,
            deep_finder_threshold: deepFinderThreshold,
            deep_finder_result_count: deepFinderResultCount,
        };
        if (!form.agent_name) {
            return;
        }

        createAgent(form)
            .then((newAgent) => {
                successToast(intl.formatMessage({ id: "t001" }));
                setIsValidated(false);
                modal.close(AGENT_CREATE_MODAL_ID);
                moveToDashboard(newAgent.id);
            })
            .catch((e) => {
                if (e.response.data?.detail === "There is an agent with the same name") {
                    errorToast(intl.formatMessage({ id: "i01018" }));
                } else {
                    errorToast(intl.formatMessage({ id: "t002" }));
                }
            });
    };

    const onClickConfirm = async (e: React.MouseEvent) => {
        e.preventDefault();
        await submit();
    };

    return (
        <Modal id={AGENT_CREATE_MODAL_ID}
               size="md"
               title={intl.formatMessage({ id: "i01001" })}
               onClose={() => setIsValidated(false)}
        >
            <div className="modal-body">
                <div className="modal-description flex flex-col gap-6">
                    <TextInputGroup defaultValue=""
                                    invalid={isValidated && !name.trim()}
                                    label={intl.formatMessage({ id: "i01002" })}
                                    placeholder={intl.formatMessage({ id: "i01003" })}
                                    onChange={setName}
                    />

                    <div className="flex flex-col gap-1.5">
                        <div className="input-group-heading">
                            {intl.formatMessage({ id: "i01004" })}
                        </div>
                        <DropdownGroup width={478}>
                            <DropdownButton>
                                {intl.formatMessage({ id: LocaleIntlKeys[locale!] })}
                            </DropdownButton>
                            <DropdownItems>
                                {LocaleTypes.map((localeType, index) => (
                                    <DropdownItem key={localeType}
                                                  isOdd={index % 2 === 1}
                                                  onClick={() => setLocale(localeType)}
                                    >
                                        {intl.formatMessage({ id: LocaleIntlKeys[localeType] })}
                                    </DropdownItem>
                                ))}
                            </DropdownItems>
                        </DropdownGroup>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <div className="input-group-heading">
                            {intl.formatMessage({ id: "i01005" })}
                        </div>
                        <DropdownGroup width={478}>
                            <DropdownButton>
                                {intl.formatMessage({ id: getLabelKeyFromValue(EMBEDDING_OPTIONS, embedding!) })}
                            </DropdownButton>
                            <DropdownItems>
                                {EMBEDDING_OPTIONS.map((option, index) => (
                                    <DropdownItem key={option.value}
                                                  isOdd={index % 2 === 1}
                                                  onClick={() => setEmbedding(option.value)}
                                    >
                                        {intl.formatMessage({ id: option.labelKey })}
                                    </DropdownItem>
                                ))}
                            </DropdownItems>
                        </DropdownGroup>
                    </div>

                    <div className="flex flex-col gap-3">
                        <div className="input-group-heading">
                            {`${intl.formatMessage({ id: "i01006" })} (${deepFinderThreshold})`}
                        </div>
                        <RangeSlider width="100%"
                                     onChange={setDeepFinderThreshold}
                                     defaultValue={50}
                        />
                    </div>


                    <div className="flex flex-col gap-1.5">
                        <div className="input-group-heading">
                            {intl.formatMessage({ id: "i01007" })}
                        </div>
                        <DropdownGroup width={478}>
                            <DropdownButton>
                                {deepFinderResultCount}
                            </DropdownButton>
                            <DropdownItems>
                                {[1, 2, 3].map((range, index) => (
                                    <DropdownItem key={range}
                                                  isOdd={index % 2 === 1}
                                                  onClick={() => setDeepFinderResultCount(range)}
                                    >
                                        {range}
                                    </DropdownItem>
                                ))}
                            </DropdownItems>
                        </DropdownGroup>
                    </div>
                </div>

                <div className="modal-btn-group">
                    <Button size="sm"
                            variant="secondary"
                            disabled={isCreating}
                            onClick={() => modal.close(AGENT_CREATE_MODAL_ID)}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button size="sm"
                            variant="primary"
                            disabled={isCreating}
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default AgentCreateModal;
